import { FormGroup } from '@angular/forms';
import { Location } from '@angular/common';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthUserService } from '../../../services/auth-user.service';
import { RegistrationFormService } from '../../../services/data/registration-form.service';
import { VisitorRegistrationFormStatus } from '../../../models/visitor-registration-form-status';
import { ConfirmModalService } from '../../../components/confirm-modal/confirm-modal.service';
import { VisitorRegistrationFormType } from '../../../models/visitor-registration-form-type';
import { ApproveCondition } from '../../../models/approve-condition.model';
import { ReviewType } from '../../../services/data/review-type';

@Component({ template: '' })
export abstract class ReviewPageBaseComponent {
  public authUserService = inject(AuthUserService);

  private _route = inject(ActivatedRoute);
  private _location = inject(Location);
  private _formService = inject(RegistrationFormService);
  private _confirmModalService = inject(ConfirmModalService);

  protected formId!: string;
  protected reviewType: ReviewType = ReviewType.Undefined;
  protected model: any = {};
  protected origModel: any = {};

  protected form = new FormGroup({});
  protected fields: FormlyFieldConfig[] = [];
  protected options: FormlyFormOptions = {
    formState: {
      disabled: true,
    },
  };

  constructor(protected formType: VisitorRegistrationFormType) {}

  ngOnInit(): void {
    this.formId = this._route.snapshot.paramMap.get('id')!;
    this.reviewType = this._route.snapshot.data['reviewType'] as ReviewType;
    this.load();
  }

  get canApprove(): Boolean {
    return this.model && !this.isOwnForm && this.isReadyForReview
  }

  get canReject(): Boolean {
    return this.model && !this.isOwnForm && this.isReadyForReview;
  }

  get isReadyForReview(): Boolean {
    return (
        this.model.form?.status === VisitorRegistrationFormStatus.Submitted ||
        this.model.form?.status === VisitorRegistrationFormStatus.InReview ||
        this.model.form?.status === VisitorRegistrationFormStatus.HRApproved ||
        this.model.form?.status === VisitorRegistrationFormStatus.EHSApproved ||
        this.model.form?.status === VisitorRegistrationFormStatus.HRApprovedForReview ||
        this.model.form?.status === VisitorRegistrationFormStatus.EHSApprovedForReview
    )
  }

  get isOwnForm(): Boolean {
    return this.model?.user?.email === this.authUserService.email;
  }

  get approveTooltip(): string {
    if (!this.model?.form?.status) {
      return 'Loading...';
    }

    return this.isOwnForm
      ? 'You cannot approve/reject your own forms'
      : 'Approve';
  }

  get rejectTooltip(): string {
    if (!this.model?.form?.status) {
      return 'Loading...';
    }

    return this.isOwnForm
      ? 'You cannot approve/reject your own forms'
      : 'Reject';
  }

  back() {
    this._location.back();
  }

  onSubmit(eventSubmitterId: string) {
    if (eventSubmitterId === 'approve') {
      this.approve();
    } else if (eventSubmitterId === 'approveToReview') {
      this.approveToReview();
    } else if (eventSubmitterId === 'reject') {
      this.reject();
    }
  }

  private load() {
    this._formService.getForm(this.formId).subscribe({
      next: (data) => {
        this.model = data;
      },
    });
  }

  private approve() {
    this._confirmModalService
      .showApproveConfirmDialog()
      .subscribe((dialogResult) => {
        if (dialogResult.result === false) {
          return;
        }

        this._formService
          .approveForm(this.formId, this.reviewType)
          .subscribe((data) => {
            this.model = data;
          });
      });
  }

  private approveToReview() {
    this._confirmModalService
      .showApproveConditionallyConfirmDialog()
      .subscribe((dialogResult) => {
        if (dialogResult.result === false) {
          return;
        }

        this._formService
          .approveConditionally(
            this.formId,
            this.reviewType,
            dialogResult.entity!,
          )
          .subscribe((data) => {
            this.model = data;
          });
      });
  }

  private reject() {
    this._confirmModalService
      .showRejectConfirmDialog()
      .subscribe((dialogResult) => {
        if (dialogResult.result === false) {
          return;
        }

        this._formService
          .rejectForm(this.formId, this.reviewType, dialogResult.entity!)
          .subscribe((data) => {
            this.model = data;
          });
      });
  }
}
